import * as Amplitude from '@amplitude/analytics-browser';

import { ProAccount, ProStripeAccount, UniverseReference } from '@front/common/src/types/commons';

import { SignupAccount, SignupResponse } from '@front/pro/src/types/pro';
import { SIGNUP_PAGE_CONFIRMED, SIGNUP_PAGE_VIEWED } from '@front/pro/src/types/tracking';

import {
  deleteAmplitudeLegacyCookie,
  getDeviceIdFromAmplitudeCookie,
} from '@front/web-ui/src/utils/amplitude-experiment';

import { RoutePath } from '../routes/routes';
import { Platform } from '../hooks/use-platform';

import { parseQueryParams } from './utils';

type TrackingArgs = { event: string; eventType?: string };

declare global {
  interface Window {
    // @ts-ignore
    dataLayer: (Record<string, any> & TrackingArgs)[];
  }
}
const getGtmDataLayer = () => window.dataLayer || [];

type UserPropertiesObject = {
  pro_score: ProAccount['score'];
  lead_score: ProAccount['lead_scoring'];
  pro_status: ProAccount['pro_status'];
  pro_status_detail: ProAccount['status_detail'];
  pro_universe: ProAccount['main_universe'];
  country_code: ProAccount['country_code'];
  payment_frequency: ProStripeAccount['payout_interval'];
  utm_source: string;
};

export const initializeTracking = (apiKey: string) => {
  // legacy cookie uses "amp_" + 6 first chars from api key
  deleteAmplitudeLegacyCookie(`amp_${apiKey.substring(0, 6)}`);

  Amplitude.init(apiKey, {
    defaultTracking: false,
  });

  const deviceId = getDeviceIdFromAmplitudeCookie(apiKey);

  if (deviceId) {
    Amplitude.setDeviceId(deviceId);
  }
};

export const setUserProperties = (userProperties: Partial<UserPropertiesObject>) => {
  const identifyObj = new Amplitude.Identify();
  Object.entries(userProperties).forEach(([key, value]) => {
    if (value) {
      identifyObj.set(key, value);
    } else {
      identifyObj.unset(key);
    }
  });
  Amplitude.identify(identifyObj);
};

export const setAmplitudeUserId = (userId: string) => {
  Amplitude.setUserId(userId);
};

export const proAccountCreated = (subscribedPro: SignupResponse) => {
  const tags = parseQueryParams(subscribedPro.pro_account.subscription_redirection_url);
  getGtmDataLayer().push({
    event: 'ProSubscription',
    universe: subscribedPro.pro_account.main_universe,
    country: subscribedPro.pro_account.country_code,
    signup_score: subscribedPro.pro_account.signup_score,
    eligible: true,
    ...tags,
  });
};

type LogSignupStepEventProps = {
  account?: Partial<SignupAccount>;
  step: RoutePath;
  universe?: UniverseReference;
};

export type StepName = 'RevenueSimulator' | 'Identity' | 'Company' | 'Availability' | 'Appointment';

const stepNameByRoutePath: Record<RoutePath, StepName | undefined> = {
  feat: undefined,
  confirmation: undefined,
  not_qualified: undefined,
  root: 'RevenueSimulator',
  register: 'Identity',
  pro_status: 'Company',
  additional_information: 'Availability',
  appointment: 'Appointment',
};

export const logSignupStepEvent = ({ account, step, universe }: LogSignupStepEventProps) => {
  const eventProperties = {
    step: stepNameByRoutePath[step],
    universe,
    professional_type: account?.company_status,
    nova: account?.nova_status,
    work_availability: account?.weekly_hours,
    nany: account?.also_a_nanny,
    nany_shift_type: account?.childcare_availability,
  };
  Amplitude.track(SIGNUP_PAGE_VIEWED, eventProperties);
};

type LogSignupConfirmationProps = {
  universe: UniverseReference;
  account: Pick<
    SignupAccount,
    'company_status' | 'declarative_source' | 'weekly_hours' | 'also_a_nanny' | 'childcare_availability'
  > & { nova: SignupAccount['nova_status'] };
};

export const logSignupConfirmation = ({ account, universe }: LogSignupConfirmationProps) => {
  Amplitude.track(SIGNUP_PAGE_CONFIRMED, {
    universe,
    ...account,
  });
};

export const logAppDownloadClicked = (appType: Platform) => {
  Amplitude.track('AppDownloadClicked', { appType });
};
